import { Component, computed, DestroyRef, effect, EffectRef, inject, OnInit, Signal, signal, untracked, ViewChild } from '@angular/core';
import { SearchInputComponent } from '../../../../../shared/search-input/search-input.component';
import { MapViewerService } from '@3ddv/ngx-dvm-internal';
import { DvmService } from '../../../../../../services/dvm.service';
import { Search, SearchOption } from '../../../../../../models/types/components/Search.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SearchService } from '../../../../../../services/components/search.service';
import { DvmFormatterPipe } from '../../../../../../helpers/pipes/dvm-formatter.pipe';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Translation } from '@models/types/components/Translation.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { orderOptions } from '@helpers/utils/order-options.util';
import { TranslationService } from '@services/translation.service';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';

@Component({
    selector: 'app-search-simple',
    imports: [
        SearchInputComponent
    ],
    providers: [DvmFormatterPipe],
    templateUrl: './search-simple.component.html',
    styleUrl: './search-simple.component.css'
})
export class SearchSimpleComponent implements OnInit {

  // VIEW CHILDS
  @ViewChild(SearchInputComponent)
  private searchInput?: SearchInputComponent;

  // SERVICES
  private   readonly SearchService: SearchService            = inject(SearchService);
  private   readonly dvmService:    DvmService               = this.SearchService.dvmService;
  private   readonly viewer:        MapViewerService         = this.dvmService.viewer;
  private   readonly view:          Signal<AppViews|null>    = this.dvmService.view;
  private   readonly uiconfig:      Signal<UiConfiguration>  = inject(UiService).uiConfig;
  public    readonly translation:   TranslationService       = inject(TranslationService);
  private   readonly destroyRef:    DestroyRef               = inject(DestroyRef);
  
  // PIPE
  private   readonly dvmFormatter:  DvmFormatterPipe         = inject(DvmFormatterPipe);

  //TRANSLATION CONFIG
  private readonly translationConfig:     Signal<Translation>         = computed((): Translation => this.uiconfig()['Translation']);
  public  readonly translationData:       Signal<Translation['data']> = computed((): Translation['data'] => this.translationConfig().data);
  public  readonly translationEnabled:    Signal<boolean>             = computed((): boolean  => this.translationConfig().enabled);
  
  //SEARCH 
  readonly searchConfig = computed(() => this.uiconfig().Search);
  public  readonly order:    Signal<Search['order']>             = computed((): Search['order']  => this.uiconfig().Search.order);
  public  readonly advancedSearchData:    Signal<Search['fileData']>             = computed((): Search['fileData']  => this.uiconfig().Search.fileData);


  // STATE
  protected readonly searchOptions = signal<SearchOption[]>([]);

  private readonly refreshOptionsOnOpen: EffectRef = effect((): void => {
    const isSearchOpen = this.SearchService.searchActive();
    const currentView = this.view();

    if (
      !isSearchOpen ||
      !this.viewer.isInitialized() ||
      (currentView !== AppViews.Topview && currentView !== AppViews.Main)
    ) return;

    this.getSectionNodes();
  });

  // Previous searchActive value. Seeded on the effect's first run so creating the component never looks like a close.
  private previousSearchActive: boolean | null | undefined = undefined;

  private readonly resetOnSearchClose: EffectRef = effect((): void => {
    const isActive:  boolean | null                = this.SearchService.searchActive(),
          wasActive: boolean | null | undefined    = this.previousSearchActive;

    this.previousSearchActive = isActive;

    // Solo reaccionamos al cierre real del buscador (true -> false), no a la creación del componente.
    if (wasActive !== true || isActive === true) return;

    untracked((): void => {
      this.searchInput?.resetInput();
      this.filterSections([]);
    });
  });

  // LIFE CYCLE HOOKS
  public ngOnInit(): void {
    this.initComponent();
  }

  // METHODS

  /**
   * Filter Sections
   * Método que escucha el evento filterValues del componente search input.
   * Primero, verifica si en el servicio hay guardado un valor de búsqueda. Si es así, lo limpia.
   * Luego, si existen valores, obtiene los ids de las secciones y los envía al servicio de DVM para establecer la disponibilidad.
   * Si no existen valores, reestablece la disponibilidad y deselecciona todas las secciones.
   * @param values 
   */
  public filterSections(values: SearchOption[]): void {

    if(this.SearchService.searchValue()){
      this.SearchService.setSearchValue(null);
    }
    
    if(values.length){
      const nodeIds: string[] = values
        .map((value: SearchOption): string => value.value)
        .filter((nodeId: string): boolean => !this.SearchService.isCustomUnavailable(AppNodes.Section, nodeId));
      this.dvmService.setAvailability('section', nodeIds);
      if (!nodeIds.length) this.selectSection(undefined);
    }else{
      const baseAvailability = this.SearchService.filterCustomAvailableNodeIds(
        AppNodes.Section,
        this.searchOptions().map((option: SearchOption): string => option.value)
      );
      this.dvmService.setAvailability('section', baseAvailability);
      this.selectSection(undefined);
    }

  }

  /**
   * Select Section
   * Método que selecciona una sección en el visor de DVM.
   * Para asegurar la consistencia, se deseleccionan todas las secciones antes de seleccionar la nueva.
   * @param value 
   */
  public selectSection(value: SearchOption | undefined): void {
    
    this.viewer.unselectAll();
    
    if(value && !this.SearchService.isCustomUnavailable(AppNodes.Section, value.value)) this.viewer.select(value.value);

  }

  /**
   * Navigate To Section
   * Método que reacciona a la selección de un valor del input search. 
   * Si viene un valor, lo guarda en el servicio de búsqueda y navega a la sección correspondiente.
   * @param value 
   */
  public navigateToSection(value: SearchOption | null): void {

    if(!value || this.SearchService.isCustomUnavailable(AppNodes.Section, value.value)) return;
    
    this.SearchService.setSearchValue(value);

    window.setTimeout((): void => this.SearchService.navigateToSearch(value), this.SearchService.duration + 250);
        
  };

  /**
   * Init Search Simple Component
   * Método que inicializa el componente search simple. Se encarga de obtener los nodos de tipo sección y mapearlos a las opciones de búsqueda.
   * Si el visor no está cargado, se suscribe al evento de carga para obtener los nodos. Si ya está cargado, se obtienen directamente.
   */
  private initComponent(): void {
    if ( this.viewer.isInitialized() ) {
      this.initSuscription();
    }
    else {
      this.viewer.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.initSuscription());
    }

  }

  private initSuscription(): void {

    this.viewer.getObservable('load_success').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(()=> {

      window.setTimeout((): void => {
        if(this.view() === AppViews.Topview || this.view() === AppViews.Main) this.getSectionNodes();
      });

    });

  }
  
  /**
   * Get Section Nodes
   * Método que obtiene los nodos de tipo sección y los mapea a las opciones de búsqueda.
   */

  getLabel(nodeId: string): string {
    const translation = this.translationConfig().enabled ? this.translationData()[nodeId] : null;
    return translation || this.dvmFormatter.transform(nodeId);
  }


  private getSectionNodes(): void {
    const sectionNodeIds = this.SearchService.filterCustomAvailableNodeIds(
      AppNodes.Section,
      this.SearchService.getSearchableNodeIds('section')
    ),
          inputOptions: SearchOption[] = sectionNodeIds.map((nodeId: string): SearchOption => ({
            label: this.getLabel(nodeId),
            value: nodeId
          }));

    const sortedOptions = orderOptions(inputOptions, 'alphabetical');

    this.searchOptions.set(sortedOptions);
    
  }

}
